import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BaseInstructionCard, BaseInstructionCardPassDownProps } from '../../InstructionListing/instructionCards/BaseInstructionCard'
import { FC, useCallback, useMemo } from 'react'

import { CreditCardOutlined } from '@mui/icons-material'
import { InstructionPrice } from '../../InstructionListing/InstructionPrice'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { PreferredPaymentMethodSwitch } from 'components/common/PreferredPaymentMethodSwitch'
import { ProductKind } from 'constants/product'
import { Stack } from '@mui/material'
import { SubscriptionInstructionUpsell } from '../../SubscriptionInstructionUpsell'
import { bigFromFee } from 'utils/price'
import { resolveInvoiceOption } from '../invoiceOption'
import { productKindIsPreferredPaymentMethod } from 'utils/typeguards'
import { useAuth0 } from 'utils/auth'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { usePurchaseFlowOrderMeta } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowOrderMeta.context'
import { useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props extends BaseInstructionCardPassDownProps {
  showSaveOption: boolean
}

/**
 * Instruction card for BILLING_PRIMARY type instructions (Billing methods).
 * Shows no price.
 * If enabled, shows if billing method is default or means of saving it as default.
 * 
 * @example
 * <BillingMethodInstructionCard
 *  showSaveOption={true}
 *  isCardDisabled={false}
 *  isSelectionDisabled={false}
 *  instructionOption={instructionOptionDTO}
 *  instructionType={InstructionType.BILLING_PRIMARY}
 *  onChange={(changed) => console.log}
 * />
 */
export const BillingMethodInstructionCard: FC<Props> = ({
  instructionOption,
  instructionType,
  isCardDisabled,
  isSelectionDisabled,
  showSaveOption = false,
}) => {
  const { t } = useTranslation(['card_new_user_invoice_paymethod', 'preferred_payment_method', 'badges'])
  const { roles } = useAuth0()
  const { targetUser } = useTargetOrderUser()
  const { clientData, hasWorkspaceActiveSubscription } = useUserData()
  const { selectedPaymentMethod, setSelectedPaymentMethod } = usePurchaseFlowOrderMeta()
  const { isEmbed } = useEmbedConfig()

  // Disallow INVOICE_BY_EMAIL for New Unsubscribed Clients
  // Some clients are allowed special paid INVOICE product, let them select it
  // Admins are allowed to order for any client via INVOICE - preferred direct method is guarded in Client step
  // One rule with the billing screen's invoice_option analytics (ISSUE-4023): the "locked" invoice is the disallowed one
  const isMethodAllowed = useMemo(() => (
    instructionOption.kind !== ProductKind.INVOICE_BY_EMAIL
    || resolveInvoiceOption({ [ProductKind.INVOICE_BY_EMAIL]: instructionOption }, { isAdmin: roles.isAdmin, invoiceAllowed: targetUser.invoiceAllowed }) !== 'locked'
  ), [instructionOption, roles.isAdmin, targetUser.invoiceAllowed])

  // Same embed dead-end as the other instruction cards: the badge stays, the CTA is dropped (ISSUE-3097)
  const showSubscriptionUpsell = !isEmbed && !isMethodAllowed

  const onCheckPaymentMethod = useCallback(() => {
    // Selection first: analytics is best-effort and must never be able to swallow the click (ISSUE-3098).
    setSelectedPaymentMethod(instructionOption.kind)

    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_PAYMENT_METHOD_SELECTED, {
      PaymentMethodSelected: instructionOption.kind,
      IsSubscribed: hasWorkspaceActiveSubscription
    })
  }, [hasWorkspaceActiveSubscription, instructionOption.kind, setSelectedPaymentMethod])

  return (
    <BaseInstructionCard
      instructionType={instructionType}
      isSelectionDisabled={isSelectionDisabled || !isMethodAllowed}
      instructionOption={instructionOption}
      isCardDisabled={isCardDisabled}
      onCheck={onCheckPaymentMethod}
      isChecked={selectedPaymentMethod === instructionOption.kind}
      rightHeaderContent={
        <Stack direction="row" alignItems="center" gap={2}>

          {instructionOption.kind === ProductKind.INVOICE_BY_EMAIL && bigFromFee(instructionOption.feePrice).gt(0) &&
            <MUIBadge
              color="coral"
              icon={<CreditCardOutlined fontSize="large" />}
              label={t('badges:free_for_subscribed')}
            />
          }

          {bigFromFee(instructionOption.feePrice).gt(0) && <InstructionPrice feePrice={instructionOption.feePrice} />}

          {showSaveOption && productKindIsPreferredPaymentMethod(instructionOption.kind)
            && isMethodAllowed
            ? <PreferredPaymentMethodSwitch paymentMethod={instructionOption.kind} />
            : undefined
          }

        </Stack>
      }
      additionalBottomBoxContent={showSubscriptionUpsell &&
        <SubscriptionInstructionUpsell
          title={t('title')}
          text={t('description')}
          onClick={() =>
            logAnalyticsEvent('unblock_invoicing', {
              userEmail: clientData?.email,
              organizationId: clientData?.organizationId,
            })
          }
        />
      }
      badges={
        isMethodAllowed
          ? []
          : [{ color: 'coral', text: t('preferred_payment_method:badge_only_subscribed'), materialIcon: <LockOutlinedIcon /> }]
      }
    >
    </BaseInstructionCard>
  )
}
